import React from "react";
import { connect } from "react-redux";
import language from "../../../language/index.js";
import { Table } from "../../Table/index.js";
import { CSVLink } from "react-csv";
import {
  fetchPatients,
  deletePatient,
  getPatientDashboardCounts,
} from "../../../utils/queries/patient.js";
import NewRecipient from "./NewPatient.js";
import { DeleteModal, Modal } from "../../Modal/index.js";
import { defineRole } from "../../../utils/handleAccessRoles.js";
import icons from "../../../constants/icons.js";
import { getStorage } from "../../../utils/storage.js";
import {
  ROLE_PATIENT,
  ROLE_PATIENT_LAB_RESULT,
} from "../../../constants/ROLES.js";
import CardCount from "../../CardCount/CardCount.js";

class Patients extends React.Component {
  csvDownload = React.createRef();

  state = {
    isLoading: false,
    data: [],
    page: 1,
    limit: 10,
    search_text: "",
    selectedData: {},
    delete_value: "",
    error: {},
    csvData: [],
    user: {},
    isLoadingCounts: false,
    counts: {
      total: 0,
      active: 0,
      inactive: 0,
    },
    showCountDetailsModal: false,
    countDetailsType: "",
    countDetailsTitle: "",
    isLoadingCountDetails: false,
    countDetailsData: [],
    countDetailsPage: 1,
    countDetailsLimit: 10,
    countDetailsTotalRecords: 0,
    countDetailsTotalPageCount: 0,
    countDetailsSearchText: "",
    searchingCountDetails: false,
    filters: {},
  };

  componentDidMount = async () => {
    const user = await getStorage();
    const hospitalFilter = user.hospital
      ? { hospital: user.hospital?._id || user.hospital }
      : {};

    this.setState({ user, filters: { ...this.state.filters, ...hospitalFilter } });

    await this.getData(true, hospitalFilter);
  };

  onChangeText(field, e) {
    let { error } = this.state;

    delete error[field];

    this.setState({
      [field]: e?.target?.value,
      error,
    });
  }

  getData = async (isLoading, search = {}) => {
    try {
      const { page, limit, filters = {} } = this.state;
      const { defaultLanguage } = this.props;

      this.setState({ isLoading, isLoadingCounts: isLoading });

      const params = {
        page,
        limit,
        ...filters,
        ...search,
      };
      const [{ data, count, totalRecords }, dashboardCounts] = await Promise.all([
        fetchPatients(defaultLanguage, params),
        getPatientDashboardCounts(defaultLanguage, search),
      ]);

      this.setState({
        data: data,
        totalPageCount: count,
        totalRecords,
        counts: {
          total: dashboardCounts?.total || 0,
          active: dashboardCounts?.active || 0,
          inactive: dashboardCounts?.inactive || 0,
        },
        isLoading: false,
        isLoadingCounts: false,
      });
    } catch (error) {
      this.setState({
        isLoading: false,
        isLoadingCounts: false,
      });
    }
  };

  handlePagination(page, _tab, limit) {
    this.setState(
      {
        page,
        ...(limit != null && { limit }),
      },
      () => {
        this.getData(true);
      }
    );
  }

  handleShowModal(modal, modalTitle, selectedData = {}) {
    this.setState({
      [modal]: true,
      modalTitle: modalTitle,
      selectedData,
    });
  }

  handleCloseModal(modal) {
    this.setState({
      [modal]: false,
    });
  }

  returnTableHeaders() {
    let headers = [
      {
        title: language[this.props.defaultLanguage].patient_id,
        key: "patientId",
        type: "link",
        onPress: (item) =>
          (window.location.href = `/dashboard/patients/${item._id}/${
            item.identityNumber || item.patientId || "patient"
          }`),
      },
      {
        title: language[this.props.defaultLanguage].patient_number,
        key: "patientBBMSNumber",
      },

      {
        title: language[this.props.defaultLanguage].first_name,
        key: "firstName",
      },
      {
        title: language[this.props.defaultLanguage].last_name,
        key: "lastName",
      },
      {
        title: language[this.props.defaultLanguage].nationality,
        key: "nationality",
      },
      {
        title: language[this.props.defaultLanguage].gender,
        key: "gender",
      },
      {
        title: language[this.props.defaultLanguage].date_of_birth,
        key: "dob",
      },
      {
        title: language[this.props.defaultLanguage].contact,
        key: "contact",
      },
      {
        title: language[this.props.defaultLanguage].identity_number,
        key: "identityNumber",
      },

      {
        title: language[this.props.defaultLanguage].father_name,
        key: "fatherName",
      },
      {
        title: language[this.props.defaultLanguage].mother_name,
        key: "motherName",
      },

      {
        title: language[this.props.defaultLanguage].created_at,
        key: "createdAt",
        isMoment: true,
        formatTime: "lll",
      },
      {
        title: language[this.props.defaultLanguage].actions,
        key: "action",
      },
    ];

    return headers;
  }

  onDelete = async () => {
    try {
      this.setState({ isDeleting: true });

      await deletePatient(
        this.props.defaultLanguage,
        this.state.selectedData._id
      );

      this.setState({ isDeleting: false });

      this.getData(true);
      this.handleCloseModal("showDeleteModal");
    } catch (error) {
      console.log(error);
      this.setState({
        isDeleting: false,
      });
    }
  };

  handleSearch = async (e) => {
    const search = e?.target?.value;
    this.setState({ search_text: search });
  };

  handleRemoteSearch = async (search) => {
    await new Promise((r) => this.setState({ searching: true, page: 1 }, r));

    await this.getData(true, {
      q: search || search !== "" ? search : undefined,
    });

    this.setState({
      searching: false,
    });
  };

  getCountDetailsTotal = (countType) => {
    if (countType === "active") return this.state.counts.active || 0;
    if (countType === "inactive") return this.state.counts.inactive || 0;
    return this.state.counts.total || 0;
  };

  getCountDetailsData = async (
    isLoadingCountDetails,
    page = this.state.countDetailsPage,
    countType = this.state.countDetailsType,
    searchText = this.state.countDetailsSearchText,
  ) => {
    try {
      this.setState({ isLoadingCountDetails });

      const { defaultLanguage } = this.props;
      const limit = this.state.countDetailsLimit;

      const filters = {
        page,
        limit,
        q: searchText && searchText !== "" ? searchText : undefined,
      };

      if (countType === "active") {
        filters.status = "active";
      }
      if (countType === "inactive") {
        filters.status = "inactive";
      }

      const response = await fetchPatients(defaultLanguage, filters);
      const data = response?.data || [];
      const totalRecords = response?.totalRecords ?? this.getCountDetailsTotal(countType);
      const totalPages = Math.ceil((totalRecords || 0) / limit);

      this.setState({
        countDetailsData: data,
        countDetailsPage: page,
        countDetailsTotalRecords: totalRecords,
        countDetailsTotalPageCount: totalPages,
        isLoadingCountDetails: false,
      });
    } catch (error) {
      this.setState({ isLoadingCountDetails: false });
    }
  };

  handleCountDetailsOpen = (countType, countTitle) => {
    const totalRecords = this.getCountDetailsTotal(countType);
    const limit = this.state.countDetailsLimit;
    this.setState(
      {
        showCountDetailsModal: true,
        countDetailsType: countType,
        countDetailsTitle: countTitle,
        countDetailsData: [],
        countDetailsPage: 1,
        countDetailsTotalRecords: totalRecords,
        countDetailsTotalPageCount: Math.ceil(totalRecords / limit),
        countDetailsSearchText: "",
        searchingCountDetails: false,
      },
      () => this.getCountDetailsData(true, 1, countType),
    );
  };

  handleCountDetailsClose = () => {
    this.setState({
      showCountDetailsModal: false,
      countDetailsType: "",
      countDetailsTitle: "",
      isLoadingCountDetails: false,
      countDetailsData: [],
      countDetailsPage: 1,
      countDetailsTotalRecords: 0,
      countDetailsTotalPageCount: 0,
      countDetailsSearchText: "",
      searchingCountDetails: false,
    });
  };

  handleCountDetailsPagination = (page) => {
    this.getCountDetailsData(true, page);
  };

  handleCountDetailsSearch = (e) => {
    this.setState({
      countDetailsSearchText: e?.target?.value || "",
    });
  };

  handleCountDetailsRemoteSearch = async () => {
    this.setState({
      searchingCountDetails: true,
    });

    await this.getCountDetailsData(
      true,
      1,
      this.state.countDetailsType,
      this.state.countDetailsSearchText,
    );

    this.setState({
      searchingCountDetails: false,
    });
  };

  returnCountDetailsHeaders = () => {
    const { defaultLanguage } = this.props;
    return [
      {
        title: language[defaultLanguage].patient_id,
        key: "patientId",
      },
      {
        title: language[defaultLanguage].patient_number,
        key: "patientBBMSNumber",
      },
      {
        title: language[defaultLanguage].first_name,
        key: "firstName",
      },
      {
        title: language[defaultLanguage].last_name,
        key: "lastName",
      },
      {
        title: language[defaultLanguage].identity_number,
        key: "identityNumber",
      },
      {
        title: language[defaultLanguage].status,
        key: "status",
      },
      {
        title: language[defaultLanguage].created_at,
        key: "createdAt",
        isMoment: true,
        formatTime: "lll",
      },
    ];
  };

  render() {
    return (
      <>
        <div className="row mb-3">
          <div className="col-md-4">
            <CardCount
              count={this.state.counts.total}
              title={`${language[this.props.defaultLanguage].total} ${language[this.props.defaultLanguage].patients}`}
              color="orange"
              icon="groups"
              isLoading={this.state.isLoadingCounts}
              onPress={() =>
                this.handleCountDetailsOpen(
                  "total",
                  `${language[this.props.defaultLanguage].total} ${language[this.props.defaultLanguage].patients}`,
                )
              }
            />
          </div>
          <div className="col-md-4">
            <CardCount
              count={this.state.counts.active}
              title={language[this.props.defaultLanguage].active}
              color="#078ECE"
              icon="check_circle"
              isLoading={this.state.isLoadingCounts}
              onPress={() =>
                this.handleCountDetailsOpen(
                  "active",
                  language[this.props.defaultLanguage].active,
                )
              }
            />
          </div>
          <div className="col-md-4">
            <CardCount
              count={this.state.counts.inactive}
              title={language[this.props.defaultLanguage].inactive}
              color="#6c757d"
              icon="block"
              isLoading={this.state.isLoadingCounts}
              onPress={() =>
                this.handleCountDetailsOpen(
                  "inactive",
                  language[this.props.defaultLanguage].inactive,
                )
              }
            />
          </div>
        </div>
        <Table
          data={this.state.data}
          isSearch
          page={this.state.page}
          limit={this.state.limit}
          search_text={this.state.search_text}
          addSearchButton
          searching={this.state.searching}
          handleSearch={this.handleSearch.bind(this)}
          handlePressSearch={() =>
            this.handleRemoteSearch(this.state.search_text)
          }
          showAdd
          addButtonText={language[this.props.defaultLanguage].add}
          handleAddPressed={this.handleShowModal.bind(
            this,
            "showModal",
            language[this.props.defaultLanguage].add
          )}
          totalPageCount={this.state.totalPageCount}
          totalRecords={this.state.totalRecords}
          isLoading={this.state.isLoading}
          handlePagination={this.handlePagination.bind(this)}
          headers={this.returnTableHeaders()}
          rowPress={(item) =>
            this.handleShowModal("showModal", item.name, item)
          }
          actions={[
            {
              canAccess: defineRole({
                roles: this.state.user.accessRole,
                menu: ROLE_PATIENT,
                operation: "update",
              }),
              name: language[this.props.defaultLanguage].edit,
              onPress: this.handleShowModal.bind(
                this,
                "showModal",
                language[this.props.defaultLanguage].edit
              ),
              icon: icons.edit,
            },
            {
              canAccess: defineRole({
                roles: this.state.user.accessRole,
                menu: ROLE_PATIENT_LAB_RESULT,
                operation: "read",
              }),
              name: language[this.props.defaultLanguage].lab_results,
              onPress: (item) =>
                (window.location.href = `/dashboard/patients/${item._id}/${item.identityNumber}`),
              icon: icons.view,
            },
            {
              canAccess: defineRole({
                roles: this.state.user.accessRole,
                menu: ROLE_PATIENT,
                operation: "update",
              }),
              name: language[this.props.defaultLanguage].delete,
              onPress: this.handleShowModal.bind(
                this,
                "showDeleteModal",
                language[this.props.defaultLanguage].delete
              ),
              icon: icons.delete,
            },
          ]}
        />
        <Modal
          handleClose={this.handleCloseModal.bind(this, "showModal")}
          show={this.state.showModal}
          title={this.state.modalTitle}
          showHeaderBottomBorder={false}
          size="lg"
        >
          <NewRecipient
            handleCloseModal={this.handleCloseModal.bind(this, "showModal")}
            getData={this.getData.bind(this)}
            {...this.state.selectedData}
          />
        </Modal>
        {/* <Modal
          handleClose={this.handleCloseModal.bind(this, "showLabResultModal")}
          show={this.state.showLabResultModal}
          title={this.state.modalTitle}
          showHeaderBottomBorder={false}
          size="lg"
        >
          <PatientInfo patientId={this.state.selectedData._id} />
        </Modal> */}
        <Modal
          handleClose={this.handleCloseModal.bind(this, "showDeleteModal")}
          show={this.state.showDeleteModal}
          title={this.state.modalTitle}
        >
          <DeleteModal
            handleCloseModal={this.handleCloseModal.bind(
              this,
              "showDeleteModal"
            )}
            error={this.state.error.delete_password}
            value={this.state.delete_value}
            onDelete={this.onDelete.bind(this)}
            onChange={(e) => this.onChangeText("delete_value", e)}
            isDeleting={this.state.isDeleting}
            name={this.state.selectedData.firstName}
            getData={this.getData.bind(this)}
          />
        </Modal>
        <CSVLink
          ref={this.csvDownload}
          filename={"Patients" + new Date().getTime()}
          data={this.state.csvData}
        ></CSVLink>
        <Modal
          handleClose={this.handleCountDetailsClose}
          show={this.state.showCountDetailsModal}
          title={this.state.countDetailsTitle}
          size="xl"
        >
          <div className="card-body">
            <Table
              data={this.state.countDetailsData}
              isSearch
              page={this.state.countDetailsPage}
              limit={this.state.countDetailsLimit}
              search_text={this.state.countDetailsSearchText}
              handleSearch={this.handleCountDetailsSearch}
              addSearchButton
              searching={this.state.searchingCountDetails}
              handlePressSearch={this.handleCountDetailsRemoteSearch}
              totalPageCount={this.state.countDetailsTotalPageCount}
              totalRecords={this.state.countDetailsTotalRecords}
              isLoading={this.state.isLoadingCountDetails}
              handlePagination={this.handleCountDetailsPagination}
              headers={this.returnCountDetailsHeaders()}
            />
          </div>
        </Modal>
      </>
    );
  }
}

const mapStateToProps = (state) => {
  const { defaultLanguage } = state.Language;
  return {
    defaultLanguage,
  };
};

export default connect(mapStateToProps)(Patients);
